import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, ArrowRight, Clock, Target, Brain, Calculator,
  BookOpen, CheckCircle, XCircle, MinusCircle, FileText,
  AlertTriangle, BarChart3, ChevronLeft, ChevronRight, Zap,
  Timer, Trophy, TrendingDown
} from "lucide-react";

const BankingMockTestPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState("select"); // select | test | results
  const [loading, setLoading] = useState(false);
  const [patterns, setPatterns] = useState([]);

  // Test state
  const [sessionId, setSessionId] = useState(null);
  const [examName, setExamName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [sectionMap, setSectionMap] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [negativeMarking, setNegativeMarking] = useState(0.25);
  const [marksPerQ, setMarksPerQ] = useState(1);

  // Timer
  const [totalTime, setTotalTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  // Results
  const [results, setResults] = useState(null);

  useEffect(() => { loadPatterns(); }, []);

  useEffect(() => {
    if (phase === "test" && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const loadPatterns = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/exam-patterns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPatterns(data.patterns || []);
    } catch { toast({ variant: "destructive", description: "Failed to load exam patterns" }); }
  };

  const startTest = async (examType) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/start?exam_type=${examType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setExamName(data.exam_name);
      setQuestions(data.questions);
      setSectionMap(data.section_map);
      setNegativeMarking(data.negative_marking);
      setMarksPerQ(data.marks_per_question);
      setTotalTime(data.total_time_minutes * 60);
      setTimeLeft(data.total_time_minutes * 60);
      setCurrentQ(0);
      setAnswers({});
      setMarked({});
      setPhase("test");
      toast({ description: `${data.exam_name} — ${data.total_questions} questions, ${data.total_time_minutes} minutes. Good luck!` });
    } catch { toast({ variant: "destructive", description: "Failed to start test" }); }
    finally { setLoading(false); }
  };

  const handleSubmit = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoading(true);
    try {
      const timeTaken = totalTime - timeLeft;
      const res = await fetch(`${API_BASE}/banking/mock-tests/submit/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          answers, questions, time_taken: timeTaken,
          negative_marking: negativeMarking, marks_per_question: marksPerQ
        })
      });
      const data = await res.json();
      setResults(data);
      setPhase("results");
    } catch { toast({ variant: "destructive", description: "Failed to submit" }); }
    finally { setLoading(false); }
  }, [sessionId, answers, questions, totalTime, timeLeft, negativeMarking, marksPerQ, token]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentQ];
  const sectionIcons = { quant: Calculator, reasoning: Brain, english: BookOpen, di: BarChart3 };

  // ─── SELECT PHASE ──────────────────────────────────────────────
  if (phase === "select") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/banking")} data-testid="mock-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Mock Tests</h1>
              <p className="text-sm text-slate-500">Full-length exam simulation with negative marking</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Negative Marking: -0.25 per wrong answer</p>
              <p className="text-sm text-amber-700">All banking exams have 0.25 marks deducted for each incorrect answer. Unattempted questions carry no penalty.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {patterns.map((p) => (
              <Card key={p.id} className="hover:shadow-lg transition-all border-slate-200 hover:border-emerald-300" data-testid={`exam-${p.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-lg text-slate-900">{p.name}</h3>
                    <Badge className="bg-red-100 text-red-700 border-red-200">-0.25</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                    <span className="flex items-center gap-1"><FileText className="w-4 h-4" /> {p.total_questions}Q</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {p.total_time_minutes} min</span>
                    <span className="flex items-center gap-1"><Target className="w-4 h-4" /> {p.sections.length} sections</span>
                  </div>
                  <div className="space-y-1 mb-4">
                    {p.sections.map((s) => (
                      <div key={s.id} className="flex justify-between text-xs text-slate-500">
                        <span>{s.name}</span>
                        <span>{s.questions}Q / {s.time_minutes}min</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => startTest(p.id)}
                    disabled={loading}
                    data-testid={`start-${p.id}`}
                  >
                    {loading ? "Loading..." : "Start Mock Test"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── TEST PHASE ────────────────────────────────────────────────
  if (phase === "test" && currentQuestion) {
    const timePercent = (timeLeft / totalTime) * 100;
    const isLowTime = timeLeft < 300;
    const attempted = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-slate-50">
        {/* Top bar with timer */}
        <div className={`sticky top-0 z-50 px-4 py-3 flex items-center justify-between border-b ${isLowTime ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs">{examName}</Badge>
            <span className="text-xs text-slate-500">Q {currentQ + 1}/{questions.length}</span>
          </div>
          <div className={`flex items-center gap-2 font-mono text-lg font-bold ${isLowTime ? 'text-red-600 animate-pulse' : 'text-slate-800'}`}>
            <Timer className="w-5 h-5" />
            {formatTime(timeLeft)}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{attempted}/{questions.length} done</span>
            <Button size="sm" variant="destructive" onClick={() => {
              if (window.confirm("Submit the test now? You cannot change answers after submitting.")) handleSubmit();
            }} data-testid="submit-test-btn">
              Submit Test
            </Button>
          </div>
        </div>

        {/* Timer progress bar */}
        <div className="w-full h-1 bg-slate-200">
          <div className={`h-full transition-all ${isLowTime ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${timePercent}%` }} />
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 flex gap-6">
          {/* Question Navigator (Left) */}
          <div className="hidden lg:block w-64 shrink-0">
            <Card className="sticky top-20">
              <CardContent className="p-4">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3">Question Navigator</h4>
                <div className="flex flex-wrap gap-1.5">
                  {questions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentQ(idx)}
                      className={`w-8 h-8 rounded text-xs font-bold transition-all ${
                        currentQ === idx ? "ring-2 ring-emerald-400 bg-emerald-600 text-white"
                        : marked[q.id] ? "bg-amber-200 text-amber-800"
                        : answers[q.id] ? "bg-emerald-100 text-emerald-800"
                        : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
                <div className="mt-4 space-y-1 text-xs text-slate-500">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Answered ({Object.keys(answers).length})</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-amber-200 border border-amber-300" /> Marked ({Object.keys(marked).length})</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-white border border-slate-200" /> Not visited ({questions.length - Object.keys(answers).length})</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question */}
          <div className="flex-1 space-y-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">{currentQuestion.mock_section_name}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{currentQuestion.difficulty}</Badge>
                </div>
                <p className="text-lg text-slate-900 leading-relaxed mt-3 whitespace-pre-wrap">{currentQuestion.question}</p>

                <div className="space-y-3 mt-6">
                  {currentQuestion.options?.map((option) => {
                    const optKey = typeof option === 'object' ? option.key : option;
                    const optText = typeof option === 'object' ? option.text : option;
                    const isSelected = answers[currentQuestion.id] === optKey;
                    return (
                      <button
                        key={optKey}
                        onClick={() => setAnswers({ ...answers, [currentQuestion.id]: optKey })}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
                        }`}
                        data-testid={`option-${optKey}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isSelected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"
                          }`}>{optKey.toUpperCase()}</span>
                          <span className="text-slate-700">{optText}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>
                      <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.min(questions.length - 1, currentQ + 1))} disabled={currentQ === questions.length - 1}>
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const { [currentQuestion.id]: _, ...rest } = answers;
                      setAnswers(rest);
                    }}>Clear</Button>
                    <Button variant={marked[currentQuestion.id] ? "default" : "outline"} size="sm"
                      className={marked[currentQuestion.id] ? "bg-amber-500 hover:bg-amber-600" : ""}
                      onClick={() => setMarked(prev => prev[currentQuestion.id] ? (() => { const { [currentQuestion.id]: _, ...r } = prev; return r; })() : { ...prev, [currentQuestion.id]: true })}>
                      {marked[currentQuestion.id] ? "Unmark" : "Mark for Review"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS PHASE ─────────────────────────────────────────────
  if (phase === "results" && results) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { setPhase("select"); setResults(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Tests
            </Button>
            <h1 className="text-2xl font-semibold text-slate-900">{examName} — Results</h1>
          </div>

          {/* Score Card */}
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-8">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                <div>
                  <p className="text-4xl font-bold text-emerald-700">{results.total_marks}</p>
                  <p className="text-sm text-slate-600">Total Marks</p>
                  <p className="text-xs text-slate-400">out of {results.max_marks}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-600 flex items-center justify-center gap-1"><CheckCircle className="w-6 h-6" /> {results.correct}</p>
                  <p className="text-sm text-slate-600">Correct</p>
                  <p className="text-xs text-green-600">+{results.positive_marks} marks</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-600 flex items-center justify-center gap-1"><XCircle className="w-6 h-6" /> {results.wrong}</p>
                  <p className="text-sm text-slate-600">Wrong</p>
                  <p className="text-xs text-red-600">-{results.negative_marks} marks</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-500 flex items-center justify-center gap-1"><MinusCircle className="w-6 h-6" /> {results.unattempted}</p>
                  <p className="text-sm text-slate-600">Unattempted</p>
                  <p className="text-xs text-slate-400">0 marks</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">{results.percentage}%</p>
                  <p className="text-sm text-slate-600">Accuracy</p>
                  <p className="text-xs text-slate-400">{Math.floor(results.time_taken / 60)} min taken</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section-wise Results */}
          {results.section_results && Object.keys(results.section_results).length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Section-wise Performance</h3>
                <div className="space-y-3">
                  {Object.entries(results.section_results).map(([secId, sec]) => (
                    <div key={secId} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{secId.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500">{sec.correct} correct, {sec.wrong} wrong, {sec.unattempted} skipped</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900">{sec.marks_obtained}/{sec.max_marks}</p>
                        <p className="text-xs text-slate-500">{sec.accuracy}% accuracy</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Review Answers */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Answer Review</h3>
              <div className="space-y-4">
                {questions.map((q, idx) => {
                  const r = results.results?.find(x => x.question_id === q.id);
                  const userAns = answers[q.id];
                  const isCorrect = r?.status === "correct";
                  const isWrong = r?.status === "wrong";
                  return (
                    <div key={idx} className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-green-200 bg-green-50' : isWrong ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="flex items-start gap-3">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isCorrect ? 'bg-green-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-slate-300 text-white'}`}>
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800 line-clamp-2">{q.question}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs">
                            {userAns && <span className={isCorrect ? 'text-green-700' : 'text-red-700'}>Your: {userAns.toUpperCase()}</span>}
                            <span className="text-green-700 font-medium">Correct: {r?.correct_answer?.toUpperCase()}</span>
                            {isWrong && <Badge className="bg-red-100 text-red-700 text-xs">-0.25</Badge>}
                            {!userAns && <span className="text-slate-400">Not attempted</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setPhase("select"); setResults(null); }}>
              Take Another Test
            </Button>
            <Button variant="outline" onClick={() => navigate("/banking")}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BankingMockTestPage;
